import { useCallback, useRef } from 'react'

export type FilterVisitMode = 'default' | 'commit' | 'sheet-close'

export interface FilterVisit {
  keepDropdownOpen: boolean
  isCurrent: () => boolean
}

/**
 * Every change inside an open mobile filter sheet applies right away. It must keep the page
 * mounted like a selection commit, or the remount would close the sheet.
 */
export const resolveFilterVisitMode = (
  isSheetOpen: boolean,
  mode: FilterVisitMode,
): FilterVisitMode => (isSheetOpen && mode === 'default' ? 'commit' : mode)

/**
 * Tracks the latest filter visit. Inertia interrupts an in-flight sync visit when a new one
 * starts, but the interrupted visit still fires onFinish, so callbacks check isCurrent before
 * touching loading state. Sheet close skips the visit when that selection was already applied.
 */
export const useFilterVisitGuard = () => {
  const lastAppliedKeyRef = useRef<string | null>(null)
  const generationRef = useRef(0)

  const beginVisit = useCallback((dedupeKey: string, mode: FilterVisitMode): FilterVisit | null => {
    if (mode === 'sheet-close' && lastAppliedKeyRef.current === dedupeKey) {
      return null
    }

    const generation = generationRef.current + 1
    generationRef.current = generation
    lastAppliedKeyRef.current = dedupeKey

    return {
      keepDropdownOpen: mode === 'commit',
      isCurrent: () => generationRef.current === generation,
    }
  }, [])

  return { beginVisit }
}
